import { useState } from 'react';
import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { BarChart as EChartsBar } from 'echarts/charts';
import { AriaComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { INSPECTIONS_BY_MONTH } from '../../data/fixtures';
import { formatNumber } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import {
  axisTokens,
  entranceAnimation,
  formatMonth,
  seriesColor,
  tooltipTokens,
  useChartMotion,
  useChartTheme,
} from './ChartTheme';

echarts.use([EChartsBar, AriaComponent, GridComponent, TooltipComponent, CanvasRenderer]);

const SERIES: Array<{ id: string; label: string; field: 'scheduled' | 'passed' }> = [
  { id: 'scheduled', label: 'Scheduled', field: 'scheduled' },
  { id: 'passed', label: 'Passed', field: 'passed' },
];

const count = (value: number) => formatNumber(value);

export function InspectionsChart() {
  const theme = useChartTheme();
  const motion = useChartMotion(theme);
  const [hidden, setHidden] = useState<string[]>([]);

  const axis = axisTokens(theme);
  const months = INSPECTIONS_BY_MONTH.map((row) => row.month);

  const legendItems: ChartLegendItem[] = SERIES.map((series, index) => ({
    id: series.id,
    label: series.label,
    color: seriesColor(theme, index),
    value: count(INSPECTIONS_BY_MONTH.reduce((total, row) => total + row[series.field], 0)),
  }));

  const series = SERIES.filter((entry) => !hidden.includes(entry.id)).map((entry) => ({
    id: entry.id,
    name: entry.label,
    type: 'bar',
    itemStyle: { color: seriesColor(theme, SERIES.findIndex((s) => s.id === entry.id)) },
    data: INSPECTIONS_BY_MONTH.map((row) => row[entry.field]),
    animationDelay: motion.delay,
  }));

  const scheduled = INSPECTIONS_BY_MONTH.reduce((total, row) => total + row.scheduled, 0);
  const passed = INSPECTIONS_BY_MONTH.reduce((total, row) => total + row.passed, 0);

  return (
    <ChartFrame
      title="Inspections"
      description="Inspections passed against inspections scheduled, per month."
      summary={`Inspections scheduled against inspections passed across ${
        INSPECTIONS_BY_MONTH.length
      } months. ${count(scheduled)} scheduled, ${count(passed)} passed.`}
      chartLabel={`Bar chart of inspections scheduled against inspections passed, ${INSPECTIONS_BY_MONTH.length} months`}
      legend={<ChartLegend label="Inspection series" items={legendItems} hidden={hidden} onToggle={(id) => setHidden((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]))} />}
      empty={series.length === 0 ? 'All inspection series are hidden. Switch one back on in the legend.' : undefined}
      table={{
        caption: 'Inspections scheduled against passed, by month',
        columns: ['Month', 'Scheduled', 'Passed'],
        rows: INSPECTIONS_BY_MONTH.map((row) => [
          formatMonth(row.month),
          count(row.scheduled),
          count(row.passed),
        ]),
      }}
    >
      <ReactEChartsCore
        echarts={echarts}
        style={{ width: '100%', height: '100%' }}
        option={{
          ...entranceAnimation(motion),
          aria: { enabled: true, label: { enabled: false }, decal: { show: true } },
          grid: { left: 4, right: 16, top: 16, bottom: 4, containLabel: true },
          tooltip: { trigger: 'axis', ...tooltipTokens(theme, count), axisPointer: { type: 'shadow' } },
          xAxis: {
            type: 'category',
            data: months,
            ...axis,
            splitLine: { show: false },
            axisLabel: { ...axis.axisLabel, hideOverlap: true, formatter: formatMonth },
          },
          yAxis: { type: 'value', ...axis, axisLabel: { ...axis.axisLabel, formatter: count } },
          series,
        }}
      />
    </ChartFrame>
  );
}
